import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@material-ui/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import { EntrepreneurContact } from '../../types/types';
import Form from '../../components/Form';
import { formikSubmitDisabled } from '../../lib/utils';
import { entPublishedContactInformation, phoneRegEx, phoneError, optionalUrlTest } from '../../lib/validationSchemas';
import { StyledText } from '../../components/StyledText';

interface EntrepreneurContactEditProps {
  contactBody: EntrepreneurContact
  open: boolean
  isPublished: boolean
  handleClose: () => void
  handleSubmit: (value: EntrepreneurContact) => Promise<void>
}

export const unpublishedContactValidationSchema = Yup.object().shape({
  contactName: Yup.string().required().trim(),
  contactTitle: Yup.string().required().trim(),
  street1: Yup.string().required().trim(),
  street2: Yup.string().nullable().trim(),
  city: Yup.string().required().trim(),
  state: Yup.string().required().trim(),
  country: Yup.string().required().trim(),
  zipCode: Yup.string().required().trim(),
  email: Yup.string().required().email(),
  phone: Yup.string().required().matches(phoneRegEx, phoneError),
  website: optionalUrlTest
});

const getValidationSchema = (isPublished: boolean) => (isPublished ? entPublishedContactInformation : unpublishedContactValidationSchema);

const EntrepreneurContactEdit: React.FC<EntrepreneurContactEditProps> = ({
  contactBody,
  open,
  isPublished,
  handleClose,
  handleSubmit
}) => {
  const initialValues = (values: EntrepreneurContact) => ({
    contactName: values.contactName || '',
    contactTitle: values.contactTitle || '',
    street1: values.street1 || '',
    street2: values.street2 || '',
    city: values.city || '',
    state: values.state || '',
    country: values.country || '',
    zipCode: values.zipCode || '',
    email: values.email || '',
    phone: values.phone || '',
    website: values.website || ''
  });

  const validationSchema = getValidationSchema(isPublished);
  const formik = useFormik({
    initialValues: initialValues(contactBody),
    validationSchema,
    enableReinitialize: true,
    onSubmit: handleSubmit
  });

  const handleCancel = () => {
    handleClose();
    formik.resetForm();
  };


  return (
    <Dialog
      open={open}
      onClose={handleClose}
    >
      <Form formik={formik}>
        <DialogTitle disableTypography>
          <StyledText variant='h2'>Edit Contact Information</StyledText>
        </DialogTitle>
        <DialogContent>
          <TextInput
            type='text'
            fullWidth
            id='contactName'
            label='Name'
            placeholder='John Smith'
            maxLength={40}
          />
          <TextInput
            type='text'
            fullWidth
            id='contactTitle'
            label='Title'
            placeholder='CEO'
            maxLength={40}
          />
          <TextInput
            type='text'
            fullWidth
            id='email'
            label='Email'
            placeholder=''
          />
          <TextInput
            type='tel'
            fullWidth
            id='phone'
            label='Phone'
            placeholder=''
            maxLength={22}
          />
          <TextInput
            type='text'
            fullWidth
            id='website'
            label='Website'
            placeholder='Website'
          />
          <TextInput
            type='text'
            fullWidth
            id='street1'
            label='Street 1'
            placeholder='Street address, P.O. box, company name, c/o'
          />
          <TextInput
            type='text'
            fullWidth
            id='street2'
            label='Street 2'
            placeholder='Apartment, suite, unit, building, floor, etc.'
          />
          <TextInput
            type='text'
            fullWidth
            id='city'
            label='City'
            placeholder='Boston'
          />
          <TextInput
            type='text'
            fullWidth
            id='state'
            label='State'
            placeholder='MA'
          />
          <TextInput
            type='text'
            fullWidth
            id='country'
            label='Country'
            placeholder='United States'
          />
          <TextInput
            type='text'
            fullWidth
            id='zipCode'
            label='Zip Code'
            placeholder='12345'
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancel}
            type='button'
            variant='text'
            size='large'
          >
            Cancel
          </Button>
          {formik.isSubmitting ? <CircularProgress />
            : (
              <Button
                type='submit'
                variant='contained'
                disableElevation
                color='primary'
                size='large'
                disabled={formikSubmitDisabled(formik)}
              >
                Update
              </Button>
            )}
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export default EntrepreneurContactEdit;
